import { useAutoAnimate } from "@formkit/auto-animate/react"
import axios from "axios"
import { useState } from "react"
import Dropzone from "react-dropzone"
import { ERRORTYPES } from "../../config"
import { getEnvConfig } from "../../env"
import convertPDF2JPG from "../convertPDF/PDF2JPG"
import Alert from "../elements/Alert"
import Spinner from "../elements/Spinner"

interface ReceiptUploadProps {
	dispatch: Function
}
const ReceiptUpload: React.FC<ReceiptUploadProps> = ({ dispatch }) => {
	const env = getEnvConfig()

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<ERRORTYPES | null>()
	const [acceptedFile, setAcceptedFile] = useState<any>(null)

	const [receiptUploadRef] = useAutoAnimate<HTMLDivElement>()

	function onDrop(dropedFiles: any) {
		setLoading(true)
		if (dropedFiles.length > 0) {
			if (dropedFiles[0].size > 8000000) {
				// 8MB
				setError(ERRORTYPES.FILESIZE)
				setLoading(false)
				try {
					// eslint-disable-next-line
					let dataLayer = window["dataLayer"] || []
					dataLayer.push({
						event: "form-submit",
						"form-name": "receipt",
						"form-submit": false,
						field_error: "upload_receipt",
					})
				} catch (e) {}
			} else {
				setError(null)

				setAcceptedFile(dropedFiles[0]) // TODO: check if this needs to be holded in the state???
				handleFileType(dropedFiles[0])
				setLoading(true)
			}
		} else {
			setError(ERRORTYPES.FILESIZE)
			setLoading(false)
			try {
				// eslint-disable-next-line
				let dataLayer = window["dataLayer"] || []
				dataLayer.push({
					event: "form-submit",
					"form-name": "receipt",
					"form-submit": false,
					field_error: "upload_receipt",
				})
			} catch (e) {}
		}
	}

	function handleFileTypeError(error: any) {
		try {
			if (error.response.data.errors.errors[0] === "Receipt already used.") {
				setError(ERRORTYPES.DOUBBLED)
			} else if (error.response.data.errors.errors[0] === "Reached maximum attempts with uploaded receipt.") {
				setError(ERRORTYPES.MAXTRIES)
			} else {
				setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
			}
		} catch (error) {
			setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
		}

		try {
			// eslint-disable-next-line
			let dataLayer = window["dataLayer"] || []
			dataLayer.push({
				event: "form-submit",
				"form-name": "receipt",
				"form-submit": false,
				field_error: "upload_receipt",
			})
		} catch (e) {}

		setLoading(false)
		setAcceptedFile(null)
	}

	function handleFileType(file) {
		setLoading(true)
		if (file.type !== "application/pdf") {
			uploadReceipt(file)
		} else {
			/* ✨ PDF magic ✨ */
			convertPDF2JPG(file)
				.then(file => {
					uploadReceipt(file)
				})
				.catch(error => {
					console.log("Error converting PDF to B64", error)
					setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
					setLoading(false)
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "receipt",
							"form-submit": false,
							field_error: "upload_receipt",
						})
					} catch (e) {}
				})
		}
	}

	async function uploadReceipt(file: any) {
		const env = getEnvConfig()
		const formdata = new FormData()
		formdata.append("file", file)
		axios
			.post(env.apibase + "uploadReceipt.php?cv=" + Date.now(), formdata, {
				headers: {
					"content-type": "multipart/form-data",
					Accept: "application/json",
				},
			})
			.then(res => {
				if (res.data.enqueued) {
					console.log("Suc", res.data)
					checkOCRStatus(res.data.temporaryReceiptDataId)
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({ event: "receipt", "receipt-upload": "upload-successful" })
					} catch (e) {}
				} else {
					console.log("NoSuc", env.apibase, res.data)
					handleFileTypeError(new Error("File submitted but something's wrong 🤔: " + JSON.stringify(res)))
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "receipt",
							"form-submit": false,
							field_error: "upload_receipt",
						})
					} catch (e) {}
				}
			})
			.catch(error => {
				console.log("Err", error)
				handleFileTypeError(error)
				setAcceptedFile(null)
			})
	}

	async function checkOCRStatus(reference: string, maxTries: number = 10) {
		axios
			.get(env.apibase + "checkReceiptUploadStatus.php?reference=" + reference + "&cv=" + Date.now())
			.then(res => {
				if (!!res.data.gptPassed) {
					// Process succeeded

					if (res.data.gptProcessed) {
						dispatch({
							type: "SET_RECEIPT_REFERENCE",
							receiptReference: reference,
							receiptFileName: res.data.processedFileName,
						})
						try {
							// eslint-disable-next-line
							let dataLayer = window["dataLayer"] || []
							dataLayer.push({ event: "form-submit", "form-name": "receipt", "form-submit": true })
						} catch (e) {}
					} else {
						setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
						setAcceptedFile(null)
					}

					setLoading(false)
				} else {
					maxTries--
					if (maxTries === 0) {
						setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
						setLoading(false)

						console.log(new Error("Too many attempts!"))
						setAcceptedFile(null)

						try {
							// eslint-disable-next-line
							let dataLayer = window["dataLayer"] || []
							dataLayer.push({
								event: "form-submit",
								"form-name": "receipt",
								"form-submit": false,
								field_error: "upload_receipt",
							})
						} catch (e) {}
					} else {
						setTimeout(() => checkOCRStatus(reference, maxTries), 3000)
					}
				}
			})
			.catch(error => {
				setError(ERRORTYPES.RECEIPT_UPLOAD_ERROR)
				setAcceptedFile(null)
				try {
					// eslint-disable-next-line
					let dataLayer = window["dataLayer"] || []
					dataLayer.push({
						event: "form-submit",
						"form-name": "receipt",
						"form-submit": false,
						field_error: "upload_receipt",
					})
				} catch (e) {}
			})
	}

	return (
		<>
			<Dropzone onDrop={onDrop} multiple={false} accept="image/jpeg,image/png,application/pdf">
				{({ getRootProps, getInputProps }) => (
					<section ref={receiptUploadRef} className="lg:mx-8">
						{!loading ? (
							<div className="flex flex-col" {...getRootProps()}>
								<input type="file" accept="image/jpeg,image/png,application/pdf" {...getInputProps()} />

								<div className="relative">
									{acceptedFile ? (
										<>
											<button className="btn btn-green bg-white text-greenDark">
												<strong>
													<img
														src={process.env.PUBLIC_URL + "/images/ico-uploaded.svg"}
														alt=""
														className="relative top-0 mr-4 inline-block h-[30px] w-[30px] "
													/>
													{acceptedFile.name}
												</strong>
											</button>
											<br />
										</>
									) : (
										<>
											{error ? (
												<>
													<span className=" block w-[100%] rounded bg-white p-8 px-20 text-center font-Roboto text-[42px] uppercase text-errorred lg:inline-block lg:w-[auto] lg:text-[50px]">
														<strong className="">Upload</strong>
														<img
															src={process.env.PUBLIC_URL + "/images/upload_error.png"}
															alt=""
															className="-mt-[10px] ml-4 inline-block h-[40px] w-[40px] flex-shrink-0 flex-grow-0"
														/>
													</span>
												</>
											) : (
												<>
													<button className="btn btn-green">
														<strong>Jetzt Bon auswählen</strong>
													</button>
												</>
											)}
										</>
									)}
								</div>
							</div>
						) : (
							<div className="flex flex-col items-center" {...getRootProps()}>
								<span className="block w-[100%] rounded bg-white p-8 px-20 text-center font-Roboto text-[42px] uppercase text-green lg:inline-block lg:w-[auto] lg:text-[50px]">
									<Spinner />
								</span>
							</div>
						)}
						{error && <Alert className={"mt-8 text-left"}>{error}</Alert>}
					</section>
				)}
			</Dropzone>
			<div className="mx-auto pt-6 text-center">
				<>
					<div className="grid grid-cols-1 gap-6 font-CalibriRegular text-black leading-[1.56] text-[17px] sm:text-[18px]">
						<p>Maximale Dateigröße: 8 MB (Formate: JPG, PNG, PDF)</p>
						<p>
							<strong className="font-CalibriBold">Folgendes muss ersichtlich sein:</strong> Kaufdatum,
							Einkaufsstätte, Aktionsprodukt inkl. Kaufpreis, Rechnungsbeleg, Rechnungsbeleg-Nummer.
						</p>
						<p>
							<strong className="font-CalibriBold">
								Bitte bewahre den originalen Kassenbon zur Gewinneinlösung auf!
							</strong>
						</p>
					</div>
				</>
			</div>

			<svg
				area-hidden="true"
				width="70"
				height="38"
				viewBox="0 0 70 38"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="absolute left-1/2 -translate-x-1/2 top-4 hidden md:block ml-[220px]"
			>
				<path
					d="M26.1379 37.4842C26.5507 37.1711 26.7213 36.63 26.5586 36.1468L24.2945 29.2925C34.4332 29.2394 45.0023 25.1526 55.0338 17.4463C63.0034 11.3274 67.7406 5.16095 67.9625 4.82985L69.1624 3.04178C69.5325 2.49235 69.4168 1.84064 68.8713 1.47459L66.9874 0.210986C66.5389 -0.087965 65.9576 -0.0753859 65.5493 0.288333C65.3771 0.439784 48.2628 15.2828 24.8516 14.7271L27.113 6.61132C27.2401 6.15464 27.0873 5.6595 26.7188 5.36008C26.3502 5.06067 25.84 5.01174 25.4157 5.23495L11.9946 12.2679L0.625373 18.5484C0.455235 18.6414 0.310571 18.7756 0.207832 18.9313C0.0886424 19.1067 0.0159423 19.3141 0.00225551 19.5303C-0.0231019 19.9444 0.168064 20.3374 0.498692 20.5786L10.9497 27.9878L24.7432 37.5173C25.1656 37.8079 25.7284 37.7934 26.1379 37.4842Z"
					fill="#005E34"
				/>
			</svg>
		</>
	)
}

export default ReceiptUpload
